<c-container class="kanban-container" data-testid="support-desk-kanban">
  <!-- Header -->
  <div class="kanban-header">
    <div class="header-left">
      <h1>Support Desk</h1>
      <span class="ticket-summary">
        {{ totalOpenTickets() }} open tickets
      </span>
    </div>
    <div class="header-right">
      <button mat-icon-button
              [matTooltip]="isVerticalView() ? 'Horizontal view' : 'Vertical view'"
              (click)="toggleView()"
              data-testid="toggle-view">
        <mat-icon>{{ isVerticalView() ? 'view_column' : 'view_agenda' }}</mat-icon>
      </button>
      <button mat-icon-button
              matTooltip="Refresh"
              (click)="refreshBoard()"
              [disabled]="isLoading"
              data-testid="refresh-board">
        <mat-icon>refresh</mat-icon>
      </button>
    </div>
  </div>

  <!-- Filters Bar -->
  <div class="filters-bar">
    <mat-form-field appearance="outline" class="search-field">
      <mat-icon matPrefix>search</mat-icon>
      <input matInput
             [formControl]="searchControl"
             placeholder="Search by name, email, or description..."
             data-testid="kanban-search">
      @if (searchControl.value) {
        <button matSuffix mat-icon-button (click)="searchControl.setValue('')">
          <mat-icon>close</mat-icon>
        </button>
      }
    </mat-form-field>

    <mat-form-field appearance="outline" class="filter-field">
      <mat-label>Issue Type</mat-label>
      <mat-select [formControl]="errorTypeControl" data-testid="kanban-error-type-filter">
        <mat-option [value]="null">All Types</mat-option>
        @for (errorType of errorTypes; track errorType.value) {
          <mat-option [value]="errorType.value">{{ errorType.name }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    @if (searchControl.value || errorTypeControl.value !== null) {
      <button mat-stroked-button (click)="clearFilters()" data-testid="clear-filters">
        <mat-icon>filter_alt_off</mat-icon>
        Clear Filters
      </button>
    }
  </div>

  <!-- Column Visibility -->
  <div class="column-visibility-bar">
    <span class="visibility-label">
      <mat-icon>visibility</mat-icon>
      Show columns:
    </span>
    <mat-checkbox
      [checked]="columnVisibility().unassigned"
      (change)="toggleColumnVisibility('unassigned')"
      data-testid="toggle-unassigned">
      Unassigned
    </mat-checkbox>
    <mat-checkbox
      [checked]="columnVisibility().assigned"
      (change)="toggleColumnVisibility('assigned')"
      data-testid="toggle-assigned">
      Assigned
    </mat-checkbox>
    <mat-checkbox
      [checked]="columnVisibility().myWork"
      (change)="toggleColumnVisibility('myWork')"
      data-testid="toggle-my-work">
      My Work
    </mat-checkbox>
    @if (canViewEscalated) {
      <mat-checkbox
        [checked]="columnVisibility().escalated"
        (change)="toggleColumnVisibility('escalated')"
        data-testid="toggle-escalated">
        Escalated
      </mat-checkbox>
    }
    <mat-checkbox
      [checked]="columnVisibility().resolved"
      (change)="toggleColumnVisibility('resolved')"
      data-testid="toggle-resolved">
      Resolved
    </mat-checkbox>
  </div>

  <!-- Kanban Board -->
  @if (isLoading) {
    <div class="loading-overlay">
      <mat-spinner diameter="48"></mat-spinner>
      <span>Loading tickets...</span>
    </div>
  } @else {
    <div class="kanban-board" [class.vertical-view]="isVerticalView()">
      <!-- Unassigned Column -->
      @if (columnVisibility().unassigned) {
        <app-kanban-column
          title="Unassigned"
          columnType="unassigned"
          icon="inbox"
          emptyMessage="No unassigned tickets"
          [tickets]="unassignedTickets()"
          [selectedTicketId]="selectedTicket?.Id ?? null"
          (ticketClicked)="onTicketClicked($event)"
          (assignToMe)="onQuickAssignToMe($event)">
        </app-kanban-column>
      }

      <!-- Assigned to Others Column -->
      @if (columnVisibility().assigned) {
        <app-kanban-column
          title="Assigned"
          columnType="assigned"
          icon="group"
          emptyMessage="No tickets assigned to others"
          [tickets]="assignedTickets()"
          [selectedTicketId]="selectedTicket?.Id ?? null"
          (ticketClicked)="onTicketClicked($event)"
          (assignToMe)="onQuickAssignToMe($event)">
        </app-kanban-column>
      }

      <!-- My Work Column -->
      @if (columnVisibility().myWork) {
        <app-kanban-column
          title="My Work"
          columnType="my-work"
          icon="person"
          emptyMessage="No tickets assigned to you"
          [tickets]="myWorkTickets()"
          [selectedTicketId]="selectedTicket?.Id ?? null"
          (ticketClicked)="onTicketClicked($event)"
          (assignToMe)="onQuickAssignToMe($event)">
        </app-kanban-column>
      }

      <!-- Escalated Column (conditional) -->
      @if (canViewEscalated && columnVisibility().escalated) {
        <app-kanban-column
          title="Escalated"
          columnType="escalated"
          icon="priority_high"
          emptyMessage="No escalated tickets"
          [tickets]="escalatedTickets()"
          [selectedTicketId]="selectedTicket?.Id ?? null"
          (ticketClicked)="onTicketClicked($event)"
          (assignToMe)="onQuickAssignToMe($event)">
        </app-kanban-column>
      }

      <!-- Resolved Column -->
      @if (columnVisibility().resolved) {
        <app-kanban-column
          title="Recently Resolved"
          columnType="resolved"
          icon="check_circle"
          emptyMessage="No recently resolved tickets"
          [tickets]="resolvedTickets()"
          [selectedTicketId]="selectedTicket?.Id ?? null"
          (ticketClicked)="onTicketClicked($event)"
          (assignToMe)="onQuickAssignToMe($event)">
        </app-kanban-column>
      }
    </div>
  }
</c-container>

<!-- Detail Panel -->
<app-ticket-detail-panel
  [ticket]="selectedTicket"
  [isOpen]="isPanelOpen"
  [isCrossGameView]="isCrossGameView"
  (closePanel)="onPanelClose()"
  (ticketUpdated)="onTicketUpdated($event)"
  (viewPlayerTickets)="onViewPlayerTickets($event)">
</app-ticket-detail-panel>
